import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Checkbox,
    FormControl,
    FormControlLabel,
    Grid2,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    TextField,
    Tooltip,
} from '@mui/material';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { MODAL_TYPE, useModals } from '../../hooks/useModal';
import {
    useGetPaymentMethod,
    addPaymentMethod,
    editPaymentMethod,
} from '@openapi/payment-methods/payment-methods';
import useNotify from '../../hooks/useNotify';
import CommonButton from '../../components/atom/CommonButton';
import PageWithTitle from '../../components/atom/PageWithTitle';
import BlockWithTitle from '../../components/atom/BlockWithTitle';
import { useTranslation } from 'react-i18next';
import { PAYMENT_METHOD_TYPES } from '../../helpers/constants';
import LanguageIcon from '@mui/icons-material/Language';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import { Typography, Box } from '@mui/material';

const EnabledStatus = ({ enabled, t }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        {enabled ? (
            <CheckIcon color="success" fontSize="small" />
        ) : (
            <CloseIcon color="error" fontSize="small" />
        )}
        <Typography variant="body2" color={enabled ? 'success.main' : 'error.main'}>
            {enabled ? t('common.enabled') : t('common.disabled')}
        </Typography>
    </Box>
);

function EditPaymentMethodPage({ newPaymentMethod }) {
    const { t } = useTranslation();
    const { paymentMethodId, venueId } = useParams();
    const { showModal } = useModals();
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();
    const [paymentMethod, setPaymentMethod] = useState(null);
    const { notifyError, notifySuccess } = useNotify();

    const {
        data: paymentMethodData,
        isLoading: paymentMethodLoading,
        error: _paymentMethodError,
    } = useGetPaymentMethod(Number(venueId), Number(paymentMethodId), {
        query: {
            enabled: !newPaymentMethod && !!paymentMethodId && !!venueId,
            onError: (err) => {
                navigate(`/venues/${venueId}/paymentmethods`);
                notifyError(err);
            },
        },
    });

    const defaultPaymentMethod = {
        slug: '',
        name: '',
        nameI18n: {},
        methodType: PAYMENT_METHOD_TYPES.CASH,
        agentOnly: false,
        noteRequired: false,
        percentage: 0,
        usd: 0,
        enabled: true,
    };

    const loadPaymentMethod = useCallback(() => {
        if (paymentMethodData) {
            setPaymentMethod({
                id: paymentMethodData.id,
                slug: paymentMethodData.slug,
                name: paymentMethodData.name,
                nameI18n: paymentMethodData.nameI18n,
                methodType: paymentMethodData.methodType,
                agentOnly: paymentMethodData.agentOnly,
                noteRequired: paymentMethodData.noteRequired,
                percentage: paymentMethodData.percentage || 0,
                usd: paymentMethodData.usd || 0,
                enabled: paymentMethodData.enabled,
            });
        }
    }, [paymentMethodData]);

    useEffect(() => {
        if (!newPaymentMethod) {
            loadPaymentMethod();
            setLoading(paymentMethodLoading);
        } else {
            setLoading(false);
        }
    }, [paymentMethodId, loadPaymentMethod, newPaymentMethod, paymentMethodLoading]);

    const onEdit = (values) => {
        showModal(MODAL_TYPE.CONFIRMATION, {
            title: t('confirmationModal.title'),
            confirm: async () => {
                try {
                    await editPaymentMethod(Number(venueId), Number(paymentMethodId), {
                        slug: values.slug,
                        name: values.name,
                        nameI18n: values.nameI18n,
                        methodType: values.methodType,
                        agentOnly: values.agentOnly,
                        noteRequired: values.noteRequired,
                        percentage: Number(values.percentage) || 0,
                        usd: Number(values.usd) || 0,
                    });
                    navigate(`/venues/${venueId}/paymentmethods`);
                    notifySuccess(t('paymentmethods.notifications.updated'));
                } catch (err) {
                    notifyError(err);
                }
            },
        });
    };

    const onSubmit = async (values) => {
        if (!newPaymentMethod) {
            return onEdit(values);
        }
        try {
            await addPaymentMethod(Number(venueId), {
                slug: values.slug,
                name: values.name,
                nameI18n: values.nameI18n,
                methodType: values.methodType,
                agentOnly: values.agentOnly,
                noteRequired: values.noteRequired,
                percentage: Number(values.percentage) || 0,
                usd: Number(values.usd) || 0,
            });
            navigate(`/venues/${venueId}/paymentmethods`);
            notifySuccess(t('paymentmethods.notifications.added'));
        } catch (error) {
            notifyError(error);
        }
    };

    if (loading) {
        return true;
    }

    const validateSchema = Yup.object().shape({
        slug: Yup.string().required(t('common.required')),
        name: Yup.string().required(t('common.required')),
        methodType: Yup.string().required(t('common.required')),
        percentage: Yup.number().min(0, t('common.positiveNumber')).default(0),
        usd: Yup.number().min(0, t('common.positiveNumber')).default(0),
    });

    return (
        <Formik
            onSubmit={onSubmit}
            initialValues={paymentMethod || { ...defaultPaymentMethod }}
            validationSchema={validateSchema}
        >
            {({ isSubmitting, values, setFieldValue, touched, errors }) => (
                <Form>
                    <PageWithTitle
                        title={
                            newPaymentMethod
                                ? t('paymentmethods.newPaymentMethodTitle')
                                : `${paymentMethod.slug}`
                        }
                    >
                        <Grid2 size={{ xs: 12 }}>
                            <BlockWithTitle title={t('paymentmethods.title')}>
                                <Grid2 size={{ xs: 12 }}>
                                    <TextField
                                        fullWidth
                                        label={t('paymentmethods.columns.slug')}
                                        value={values.slug}
                                        onChange={(ev) => setFieldValue('slug', ev.target.value)}
                                        error={touched.slug && Boolean(errors.slug)}
                                        helperText={touched.slug ? errors.slug : ''}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 10, sm: 11 }}>
                                    <TextField
                                        fullWidth
                                        label={t('paymentmethods.columns.name')}
                                        value={values.name}
                                        onChange={(ev) => setFieldValue('name', ev.target.value)}
                                        error={touched.name && Boolean(errors.name)}
                                        helperText={touched.name ? errors.name : ''}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 2, sm: 1 }} sx={{ alignContent: 'center' }}>
                                    <Tooltip title={t('common.translations')}>
                                        <IconButton
                                            onClick={() => {
                                                showModal(MODAL_TYPE.TRANSLATIONS, {
                                                    translations: values.nameI18n,
                                                    setTranslations: (translations) =>
                                                        setFieldValue('nameI18n', translations),
                                                    item: 'paymentmethods',
                                                    column: 'name',
                                                    venueId: venueId,
                                                    originalString: values.name,
                                                });
                                            }}
                                        >
                                            <LanguageIcon />
                                        </IconButton>
                                    </Tooltip>
                                </Grid2>
                                <Grid2 size={{ xs: 12 }}>
                                    <FormControl fullWidth>
                                        <InputLabel id="methodType">
                                            {t('paymentmethods.columns.methodType')}
                                        </InputLabel>
                                        <Select
                                            sx={{ textAlign: 'left' }}
                                            fullWidth
                                            label={t('paymentmethods.columns.methodType')}
                                            labelId="methodType"
                                            value={values.methodType}
                                            onChange={(ev) =>
                                                setFieldValue('methodType', ev.target.value)
                                            }
                                        >
                                            {Object.entries(PAYMENT_METHOD_TYPES).map(
                                                ([key, methodType]) => (
                                                    <MenuItem value={methodType} key={key}>
                                                        {t(
                                                            `paymentmethods.methodTypes.${key.toLowerCase()}`,
                                                        )}
                                                    </MenuItem>
                                                ),
                                            )}
                                        </Select>
                                    </FormControl>
                                </Grid2>
                                <Grid2 size={{ xs: 12 }}>
                                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={values.agentOnly}
                                                    onChange={(e) =>
                                                        setFieldValue('agentOnly', e.target.checked)
                                                    }
                                                />
                                            }
                                            label={t('paymentmethods.columns.agentOnly')}
                                            labelPlacement="end"
                                        />
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={values.noteRequired}
                                                    onChange={(e) =>
                                                        setFieldValue(
                                                            'noteRequired',
                                                            e.target.checked,
                                                        )
                                                    }
                                                />
                                            }
                                            label={t('paymentmethods.columns.noteRequired')}
                                            labelPlacement="end"
                                        />
                                    </Box>
                                </Grid2>
                                <Grid2 size={{ xs: 6 }}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label={t('paymentmethods.columns.percentage')}
                                        value={values.percentage}
                                        onChange={(ev) =>
                                            setFieldValue('percentage', ev.target.value)
                                        }
                                        inputProps={{ min: 0, step: 0.01 }}
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 6 }}>
                                    <TextField
                                        fullWidth
                                        type="number"
                                        label={t('paymentmethods.columns.usd')}
                                        value={values.usd}
                                        onChange={(ev) => setFieldValue('usd', ev.target.value)}
                                        inputProps={{ min: 0, step: 0.01 }}
                                    />
                                </Grid2>
                                {!newPaymentMethod &&
                                    values.methodType === PAYMENT_METHOD_TYPES.CARD && (
                                        <Grid2 size={{ xs: 12 }}>
                                            <Box sx={{ mt: 1, textAlign: 'left' }}>
                                                <Box
                                                    sx={{
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        gap: 2,
                                                        mb: 0.5,
                                                    }}
                                                >
                                                    <Typography variant="subtitle2">
                                                        Stripe Connect
                                                    </Typography>
                                                    <EnabledStatus enabled={values.enabled} t={t} />
                                                </Box>
                                                {paymentMethodData?.stripeConnectAccountId ? (
                                                    <Typography
                                                        variant="body2"
                                                        color="success.main"
                                                    >
                                                        Connected:{' '}
                                                        {paymentMethodData.stripeConnectAccountId}
                                                    </Typography>
                                                ) : (
                                                    <Box>
                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{ mb: 1 }}
                                                        >
                                                            Not connected. Share this onboarding
                                                            link with the Connect partner:
                                                        </Typography>
                                                        <TextField
                                                            fullWidth
                                                            size="small"
                                                            value={
                                                                paymentMethodData?.venueSlug &&
                                                                paymentMethodData?.connectOnboardingHash
                                                                    ? `${window.location.origin}/connect-onboarding/${paymentMethodData.venueSlug}/${paymentMethodId}/${paymentMethodData.connectOnboardingHash}`
                                                                    : ''
                                                            }
                                                            slotProps={{
                                                                input: { readOnly: true },
                                                            }}
                                                            onClick={(e) => e.target.select()}
                                                        />
                                                    </Box>
                                                )}
                                            </Box>
                                        </Grid2>
                                    )}
                                {(newPaymentMethod ||
                                    values.methodType !== PAYMENT_METHOD_TYPES.CARD) && (
                                    <Grid2 size={{ xs: 12 }}>
                                        <EnabledStatus enabled={values.enabled} t={t} />
                                    </Grid2>
                                )}
                                <Grid2 size={{ xs: 6, md: 2 }}>
                                    <CommonButton
                                        fullWidth
                                        onClick={() =>
                                            navigate(`/venues/${venueId}/paymentmethods`)
                                        }
                                        label={t('common.cancel')}
                                        buttonType="secondary"
                                    />
                                </Grid2>
                                <Grid2 size={{ xs: 6, md: 2 }}>
                                    <CommonButton
                                        fullWidth
                                        disabled={isSubmitting}
                                        type="submit"
                                        label={t('common.save')}
                                        buttonType="primary"
                                    />
                                </Grid2>
                            </BlockWithTitle>
                        </Grid2>
                    </PageWithTitle>
                </Form>
            )}
        </Formik>
    );
}

export default EditPaymentMethodPage;
